/**
 * Sprint 19 — Production-safe delete button.
 *
 * Wraps every "trash can" icon with a mandatory confirmation dialog so a
 * misplaced click can no longer wipe an invoice, supplier, staff row, or
 * inventory item.
 *
 * Usage:
 *   <ConfirmDeleteButton
 *     onConfirm={() => remove(row.id)}
 *     itemName={row.name}
 *     data-testid={`del-inv-${row.id}`}
 *   />
 */
import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useI18n } from "@/context/I18nContext";

const L = {
  tr: {
    title: "Silmek istediğine emin misin?",
    body: (name) => name
      ? `"${name}" kaydı kalıcı olarak silinecek.`
      : "Bu kayıt kalıcı olarak silinecek.",
    cancel: "Vazgeç",
    delete: "Sil",
  },
  de: {
    title: "Wirklich löschen?",
    body: (name) => name
      ? `"${name}" wird dauerhaft gelöscht.`
      : "Dieser Eintrag wird dauerhaft gelöscht.",
    cancel: "Abbrechen",
    delete: "Löschen",
  },
};

export default function ConfirmDeleteButton({
  onConfirm,
  itemName,
  disabled = false,
  size = "icon",
  variant = "ghost",
  className = "h-8 w-8 rounded-lg hover:bg-destructive/10",
  iconClassName = "w-4 h-4 text-destructive",
  "data-testid": testid,
  children,
}) {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const t = L[lang] || L.tr;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        className={className}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        disabled={disabled}
        data-testid={testid}
      >
        {children || <Trash2 className={iconClassName} />}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid={testid ? `${testid}-dialog` : undefined}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.title}</AlertDialogTitle>
            <AlertDialogDescription>{t.body(itemName)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid={testid ? `${testid}-cancel` : undefined}
            >{t.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={busy}
              data-testid={testid ? `${testid}-confirm` : undefined}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              <Trash2 className="w-4 h-4 mr-2" /> {t.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
